import { humanizeToken, readableParts } from "@sx/ui";

import type { EpisodeUseFilter } from "./api/types";

/** What an episode is for, as its own file declares it. An evaluation episode is held out:
 *  a dataset version never trains on it, so it reads as held out, not as a kind of use. */
export const EPISODE_USE_WORDS: Record<EpisodeUseFilter, string> = {
  training: "Training",
  evaluation: "Held out for testing",
};

/** An episode's declared use in words, or that its file declares none. */
export function episodeUseWords(use: EpisodeUseFilter | null): string {
  return use === null ? "Not declared" : EPISODE_USE_WORDS[use];
}

/** A catalog name as a person reads it. Catalog names are dotted keys
 *  (`corpora.kitchen.pick-place`); the last segment is the record's own name, read as
 *  words, with digest fragments dropped. Undefined when only a digest is left — the
 *  caller then names the record by its kind and time instead. */
export function catalogName(name: string): string | undefined {
  const leaf = name.split(".").at(-1) ?? name;
  const words = readableParts(leaf).join(" ");
  return words ? humanizeToken(words) : undefined;
}

/** A segment as a person reads it: its path-like id's human parts, outermost first
 *  (`distractor-e42c05c81ac1/episode-00` → ["Distractor", "Episode 00"];
 *  `segment:assembly-demo:000042` → ["Assembly demo", "000042"]). Empty when the id is
 *  opaque. */
export function segmentNameParts(segmentId: string): string[] {
  return readableParts(segmentId.replace(/:/g, "/"))
    .filter((part) => part.toLowerCase() !== "segment")
    .map(humanizeToken);
}

/** One line naming a segment, or undefined when its id is opaque. */
export function segmentName(segmentId: string): string | undefined {
  const parts = segmentNameParts(segmentId);
  return parts.length ? parts.join(" ") : undefined;
}

/** A robot's embodiment key as words: `panda_omron` → "Panda omron",
 *  `embodiment:piper:4` → "Piper 4". */
export function embodimentName(embodimentId: string): string {
  return humanizeToken(
    embodimentId.replace(/^embodiment:/, "").replace(/[:/]/g, " "),
  );
}

/** A task reference as words: `pour.water@<sha256>` → "Pour water". The pinned digest is
 *  the reference's version, never its name. */
export function taskName(taskRef: string): string {
  const id = taskRef.split("@")[0] ?? taskRef;
  return id ? humanizeToken(id) : "Unnamed task";
}

/** A contract's revisioned id as words: `sx.episode/v9` → "Episode, version 9",
 *  `sx.yubi-episode-mcap/v1` → "Yubi episode MCAP, version 1". */
export function contractName(id: string): string {
  const match = /^(?:sx\.)?(.+)\/v(\d+)$/.exec(id);
  if (match === null) return humanizeToken(id);
  const name = humanizeToken(match[1]!).replace(/\b(mcap|rrd)\b/gi, (format) =>
    format.toUpperCase(),
  );
  return `${name}, version ${match[2]}`;
}
